import React, { useState, useEffect } from 'react';
import { Search, X, Clock, Hash, User, Calendar, Tag } from 'lucide-react';
import { Epic, Story, Sprint } from '../types';

interface SearchResult {
  type: 'epic' | 'story' | 'sprint' | 'person';
  id: string;
  title: string;
  subtitle?: string;
  icon: React.ReactNode;
  metadata?: string;
}

interface UniversalSearchProps {
  epics: Epic[];
  stories: Story[];
  sprints: Sprint[];
  onSelect: (result: SearchResult) => void;
}

export const UniversalSearch: React.FC<UniversalSearchProps> = ({
  epics,
  stories,
  sprints,
  onSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Search across all data
  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      return;
    }

    const searchLower = query.toLowerCase();
    const newResults: SearchResult[] = [];

    // Search epics
    epics.forEach(epic => {
      if (
        epic.title.toLowerCase().includes(searchLower) ||
        epic.id.toLowerCase().includes(searchLower) ||
        epic.description?.toLowerCase().includes(searchLower)
      ) {
        newResults.push({
          type: 'epic',
          id: epic.id,
          title: epic.title,
          subtitle: `${epic.completedStories}/${epic.totalStories} stories completed`,
          icon: <Hash className="w-4 h-4" />,
          metadata: epic.status,
        });
      }
    });

    // Search stories
    stories.forEach(story => {
      if (
        story.title.toLowerCase().includes(searchLower) ||
        story.id.toLowerCase().includes(searchLower) ||
        story.description?.toLowerCase().includes(searchLower)
      ) {
        newResults.push({
          type: 'story',
          id: story.id,
          title: story.title,
          subtitle: `${story.points} points • ${story.assignee}`,
          icon: <Tag className="w-4 h-4" />,
          metadata: story.status,
        });
      }
    });

    // Search sprints
    sprints.forEach(sprint => {
      if (sprint.name.toLowerCase().includes(searchLower)) {
        newResults.push({
          type: 'sprint',
          id: sprint.id,
          title: sprint.name,
          subtitle: `${sprint.startDate} - ${sprint.endDate}`,
          icon: <Calendar className="w-4 h-4" />,
          metadata: sprint.status,
        });
      }
    });

    setResults(newResults.slice(0, 50)); // Limit to 50 results
    setSelectedIndex(0);
  }, [query, epics, stories, sprints]);

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ⌘/Ctrl + F to open search
      if ((e.metaKey || e.ctrlKey) && e.key === 'f') {
        e.preventDefault();
        setIsOpen(true);
      }

      if (!isOpen) return;

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setSelectedIndex(prev => 
            prev < results.length - 1 ? prev + 1 : prev
          );
          break;
        case 'ArrowUp':
          e.preventDefault();
          setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
          break;
        case 'Enter':
          e.preventDefault();
          if (results[selectedIndex]) {
            onSelect(results[selectedIndex]);
            setIsOpen(false);
            setQuery('');
          }
          break;
        case 'Escape':
          e.preventDefault();
          setIsOpen(false);
          setQuery('');
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, results, selectedIndex, onSelect]);

  if (!isOpen) {
    return (
      <button
        onClick={() => setIsOpen(true)}
        className="flex items-center gap-2 px-4 py-2 glass apple-card-glass rounded-apple-lg hover:bg-system-fill/50 transition-all duration-200"
      >
        <Search className="w-4 h-4 text-system-label-secondary" />
        <span className="apple-body text-system-label-secondary">
          Search everything...
        </span>
        <kbd className="ml-2 px-2 py-1 apple-caption2 text-system-label-tertiary bg-system-fill rounded-apple">
          ⌘F
        </kbd>
      </button>
    );
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh] bg-black/50 backdrop-blur-sm animate-in fade-in duration-200"
      onClick={() => {
        setIsOpen(false);
        setQuery('');
      }}
    >
      <div
        className="w-full max-w-3xl glass-heavy rounded-apple-2xl shadow-apple-modal overflow-hidden animate-in slide-in-from-top-4 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Search Header */}
        <div className="flex items-center gap-3 p-4 border-b border-system-separator">
          <Search className="w-5 h-5 text-system-label-tertiary flex-shrink-0" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search epics, stories, sprints, people..."
            className="flex-1 apple-headline text-system-label bg-transparent outline-none placeholder-system-label-tertiary"
            autoFocus
          />
          <button
            onClick={() => {
              setIsOpen(false);
              setQuery('');
            }}
            className="p-1 hover:bg-system-fill rounded-apple transition-colors"
          >
            <X className="w-5 h-5 text-system-label-secondary" />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto apple-scrollbar">
          {!query.trim() ? (
            <div className="p-8 text-center">
              <Search className="w-12 h-12 text-system-label-tertiary mx-auto mb-4 opacity-50" />
              <p className="apple-headline text-system-label mb-2">
                Universal Search
              </p>
              <p className="apple-body text-system-label-secondary">
                Search across all epics, stories, sprints, and team members
              </p>
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                {['Epic', 'Story', 'Sprint', 'Assignee'].map(tag => (
                  <span
                    key={tag}
                    className="px-3 py-1 apple-caption1 text-system-label-secondary bg-system-fill rounded-apple-lg"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ) : results.length === 0 ? (
            <div className="p-8 text-center">
              <p className="apple-body text-system-label-secondary">
                No results found for "{query}"
              </p>
            </div>
          ) : (
            <div className="p-2">
              {results.map((result, index) => {
                const isSelected = index === selectedIndex;
                
                return (
                  <button
                    key={`${result.type}-${result.id}`}
                    onClick={() => {
                      onSelect(result);
                      setIsOpen(false);
                      setQuery('');
                    }}
                    onMouseEnter={() => setSelectedIndex(index)}
                    className={`
                      w-full flex items-center gap-3 p-3 rounded-apple-lg
                      transition-all duration-150 text-left
                      ${isSelected 
                        ? 'bg-apple-blue/10 border-l-2 border-apple-blue' 
                        : 'hover:bg-system-fill/50'
                      }
                    `}
                  >
                    <div className={`
                      flex-shrink-0 w-10 h-10 rounded-apple flex items-center justify-center
                      ${isSelected ? 'bg-apple-blue text-white' : 'bg-system-fill text-system-label-secondary'}
                    `}>
                      {result.icon}
                    </div>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <p className="apple-callout text-system-label font-medium truncate">
                          {result.title}
                        </p>
                        {result.metadata && (
                          <span className={`
                            px-2 py-0.5 apple-caption2 rounded-apple-lg flex-shrink-0
                            ${result.metadata === 'Done' ? 'bg-apple-green/20 text-apple-green' :
                              result.metadata === 'In Progress' ? 'bg-apple-blue/20 text-apple-blue' :
                              'bg-system-fill text-system-label-secondary'}
                          `}>
                            {result.metadata}
                          </span>
                        )}
                      </div>
                      {result.subtitle && (
                        <p className="apple-caption1 text-system-label-secondary truncate">
                          {result.subtitle}
                        </p>
                      )}
                    </div>

                    <span className={`
                      px-2 py-1 apple-caption2 text-system-label-tertiary rounded-apple flex-shrink-0
                      ${isSelected ? 'bg-white/20' : 'bg-system-fill'}
                    `}>
                      {result.type}
                    </span>
                  </button>
                );
              })}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-3 border-t border-system-separator bg-system-surface-secondary">
          <div className="flex items-center justify-between apple-caption1 text-system-label-tertiary">
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-system-fill rounded">↑↓</kbd>
                Navigate
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-system-fill rounded">↵</kbd>
                Select
              </span>
              <span className="flex items-center gap-1">
                <kbd className="px-1.5 py-0.5 bg-system-fill rounded">esc</kbd>
                Close
              </span>
            </div>
            {results.length > 0 && (
              <span>{results.length} results</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
